import { useEffect, useState } from 'react';
import { Alert, Button, Card, cn, Modal, useInitialFocus } from '@playerbase/ui';
import type { GameDevStage } from '@playerbase/types';

import { STAGE_BADGE_CLASSES } from '../../../games/stage-colour-classes.js';

type DevStageRemoveDialogProps = {
  open: boolean;
  stage: GameDevStage | null;
  otherStages: GameDevStage[];
  buildsCount: number;
  busy: boolean;
  onCancel: () => void;
  // When buildsCount is 0, targetStageId is null. Otherwise the dialog
  // gates the confirm button until the dev picks a target.
  onConfirm: (targetStageId: string | null) => void;
};

export function DevStageRemoveDialog({
  open,
  stage,
  otherStages,
  buildsCount,
  busy,
  onCancel,
  onConfirm,
}: DevStageRemoveDialogProps): JSX.Element {
  const cancelRef = useInitialFocus<HTMLButtonElement>();
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (open) setTarget(otherStages[0]?.id ?? null);
  }, [open, otherStages]);

  const inUse = buildsCount > 0;
  const canConfirm = !inUse || target !== null;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      busy={busy}
      labelledById="remove-stage-title"
      initialFocusRef={cancelRef}
    >
      <Card>
        <Card.Header>
          <Card.Title id="remove-stage-title">
            Remove {stage?.label ?? 'stage'}?
          </Card.Title>
          <Card.Description>
            {inUse
              ? `${buildsCount} build${buildsCount === 1 ? '' : 's'} currently use this stage. Pick a stage to migrate them to.`
              : 'No builds use this stage, so removing it is safe.'}
          </Card.Description>
        </Card.Header>
        <Card.Content>
          {inUse && (
            <div className="space-y-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-text-muted">
                Migrate to
              </div>
              {otherStages.length === 0 ? (
                <Alert tone="danger">
                  This is the last remaining stage. Add another stage before removing this one.
                </Alert>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {otherStages.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setTarget(s.id)}
                      className={cn(
                        'rounded-sm border px-2 py-1 text-[11px] font-mono uppercase tracking-wider transition-opacity',
                        STAGE_BADGE_CLASSES[s.colour],
                        target === s.id ? 'opacity-100 ring-1 ring-accent' : 'opacity-60 hover:opacity-100',
                      )}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-text-muted">
                Each affected build gets a stage-change log entry recording the migration.
              </p>
            </div>
          )}
        </Card.Content>
        <Card.Footer className="justify-end">
          <Button
            ref={cancelRef}
            variant="ghost"
            size="md"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="md"
            onClick={() => onConfirm(inUse ? target : null)}
            disabled={busy || !canConfirm}
          >
            {busy ? 'Removing…' : 'Remove stage'}
          </Button>
        </Card.Footer>
      </Card>
    </Modal>
  );
}
